///
/// EarthBrowser
///
/// Created by Matt Giger on 9/20/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Crash detection via the dirty-flag pattern. Mobile browsers (especially
/// iOS Safari) jettison a tab that exhausts memory with no event, callback
/// or error - the only observable signal is the *absence* of a clean
/// shutdown. Each tab writes an "alive" entry to localStorage, refreshes it
/// on a heartbeat while foregrounded, and marks itself clean on pagehide or
/// backgrounding (both fire on every legitimate exit path; neither fires on
/// a foreground memory kill).
///
/// Two ways an "alive" entry is recognized as a crash at the next launch:
///
/// - Same tab: the tab's own previous session id is kept in sessionStorage,
///   which survives the automatic reload browsers do after killing a
///   renderer (Safari's WebContent process, Chrome's "Aw, Snap", a
///   Capacitor WKWebView). An "alive" entry under that id is a crash no
///   matter how fresh it is - this is what catches the immediate reload
///   loop, where the entry is seconds old.
/// - Stale: an "alive" entry nobody has heartbeated for a few periods
///   belongs to a dead session (a tab killed and not reloaded, or closed by
///   force-quit). Fresh entries under other ids are other live tabs.
///
/// What happens after a detection is decided by CrashRecovery, not here.
/// The sentinel only keeps the facts recovery needs: how many crashes have
/// happened inside the loop window (consecutive crashes within half an
/// hour escalate; a session that proves stable clears the history), which
/// heavy layers were on when the tab died and in what order they were
/// enabled, whether a staggered restore was mid-flight (and of which
/// layer), and the breadcrumb trail of the dead session.
///
/// Breadcrumbs: since a memory kill leaves no console output behind,
/// recordCrumb() writes a trail of instrumented checkpoints (with GPU
/// texture and, where available, JS heap numbers) synchronously to
/// localStorage. On the next launch the crashed session's trail is
/// recovered, so the last crumb pinpoints which allocation the tab died in.

import { textureMemoryStats } from "@stovokor/shader/TextureMemory"

const cStorageKey = "eb_crash_sentinel"
/** sessionStorage: this tab's session id, read back after a same-tab reload */
const cTabSessionKey = "eb_crash_tab_session"
const cCrumbKeyPrefix = "eb_crash_crumbs:"
const cMaxCrumbs = 150
const cHeartbeatMs = 15_000
/** An "alive" entry older than this belongs to a dead session, not a live tab */
const cStaleMs = cHeartbeatMs * 4
/** Crashes this close together form a loop and escalate recovery; older
 *  ones are forgotten. A loop on a phone plays out in seconds to minutes;
 *  the window is generous so a user who gives up and comes back later
 *  still gets the escalated recovery instead of a repeat of level 1 */
const cLoopWindowMs = 30 * 60 * 1000
/** Deepest recovery level (CrashRecovery: 1 stagger, 2 lite, 3 start light) */
export const cMaxCrashLevel = 3

type SessionState = "alive" | "clean"

interface SessionEntry {
	ts: number
	state: SessionState
	/** Heavy (texture-series) layers enabled, in the order they came on.
	 *  Entries written before this was a list hold a boolean */
	heavy?: string[] | boolean
	/** Heavy layer whose staggered restore was in flight when last marked */
	restoring?: string
	/** Set when the page turned a detectable failure (WebGL context loss)
	 *  into a controlled reload; absent for a true silent kill */
	reason?: string
}

interface SentinelRecord {
	sessions: Record<string, SessionEntry>
	/** Detection times of the crashes inside the loop window */
	crashes: number[]
	/** Heavy layers a recovery took off the globe and has not given back
	 *  yet (a crash mid-stagger, or a "start light" launch), so a further
	 *  crash still knows the user's full set */
	pending: string[]
}

/** What the previous session's death looked like, decided once at init */
export interface CrashVerdict {
	/** 0 = clean start; 1..cMaxCrashLevel = consecutive crashes in the loop window */
	level: number
	/** Heavy layers on when the tab died plus any an earlier recovery still owed */
	heavyLayers: string[]
	/** The layer most likely responsible: the one whose restore was in
	 *  flight, else the most recently enabled heavy layer */
	suspect: string | null
	/** The dead session was this very tab (reloaded by the browser) */
	sameTab: boolean
	/** Why the previous session ended, when it said so itself (e.g.
	 *  "webgl-context-lost"); null for a silent kill */
	reason: string | null
	/** Age in ms of every crash still inside the loop window, this one included */
	history: number[]
}

let sessionId = ""
let verdict: CrashVerdict = { level: 0, heavyLayers: [], suspect: null, sameTab: false, reason: null, history: [] }
let initialized = false
let bootTimeMs = 0
let heavyLayersOn: string[] = []
let restoringLayer: string | null = null
/** Set by reloadAsCrash: the pagehide of the reload must not mark us clean */
let crashReason: string | null = null
const crumbs: string[] = []
let recoveredTrail: string[] | null = null

function emptyRecord(): SentinelRecord {
	return { sessions: {}, crashes: [], pending: [] }
}

function stringList(value: unknown): string[] {
	return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []
}

function readRecord(): SentinelRecord {
	try {
		const raw = localStorage.getItem(cStorageKey)
		if (!raw) return emptyRecord()
		const parsed = JSON.parse(raw) as Partial<SentinelRecord>
		return {
			sessions: typeof parsed.sessions === "object" && parsed.sessions !== null ? parsed.sessions : {},
			crashes: Array.isArray(parsed.crashes) ? parsed.crashes.filter((t): t is number => typeof t === "number") : [],
			pending: stringList(parsed.pending),
		}
	} catch {
		return emptyRecord()
	}
}

function writeRecord(record: SentinelRecord): void {
	try {
		localStorage.setItem(cStorageKey, JSON.stringify(record))
	} catch {
		// localStorage full or unavailable: detection degrades gracefully
	}
}

/** Mark this tab's session entry. Synchronous localStorage only - on iOS
 *  backgrounding there is no guaranteed async runway. */
function markSession(state: SessionState): void {
	if (!sessionId) return
	const record = readRecord()
	record.sessions[sessionId] = {
		ts: Date.now(),
		state: crashReason ? "alive" : state,
		heavy: heavyLayersOn,
		restoring: restoringLayer ?? undefined,
		reason: crashReason ?? undefined,
	}
	writeRecord(record)
}

/**
 * Turn a failure the page can see (WebGL context loss) into a controlled
 * reload that the next launch treats as a crash of this tab: the session
 * stays "alive" through the reload's pagehide, so the same-tab rule finds
 * it and CrashRecovery brings the heavy layers back staggered. A dead
 * context can't be used again; the alternative is a frozen black canvas.
 */
export function reloadAsCrash(reason: string): void {
	if (crashReason) return
	crashReason = reason
	recordCrumb(`crash:${reason}`)
	markSession("alive")
	window.location.reload()
}

/** Update one field of this session's entry without touching its timestamp */
function patchSession(patch: Partial<SessionEntry>): void {
	if (!sessionId) return
	const record = readRecord()
	const entry = record.sessions[sessionId]
	if (!entry) return
	Object.assign(entry, patch)
	writeRecord(record)
}

/**
 * Record which heavy (texture-series) datasets are currently enabled.
 * Called by memoryGateMiddleware after every store update. The list keeps
 * enable order (existing layers stay put, new ones append) so the next
 * launch can point at the most recently added one as the likely culprit.
 * Idempotent on unchanged sets (localStorage writes are synchronous).
 */
export function noteHeavyLayers(layers: string[]): void {
	const current = new Set(layers)
	const next = heavyLayersOn.filter(layer => current.has(layer))
	for (const layer of layers) {
		if (!next.includes(layer)) next.push(layer)
	}
	if (next.length === heavyLayersOn.length && next.every((layer, i) => layer === heavyLayersOn[i])) return
	heavyLayersOn = next
	patchSession({ heavy: heavyLayersOn })

	// Chromium's Reporting API attaches this key-value store to the
	// out-of-band crash report it sends after an OOM kill, so the server
	// learns which layers were on without a client that is already dead
	const crashReport = (window as Window & { crashReport?: { set?: (key: string, value: string) => void } }).crashReport
	try {
		crashReport?.set?.("layers", heavyLayersOn.join(","))
	} catch { }
}

/** Heavy layers currently enabled, in enable order */
export function heavyLayersEnabled(): string[] {
	return [...heavyLayersOn]
}

/**
 * Flag the heavy layer whose staggered restore is in flight (null when
 * none). If the tab dies with this set, the next launch blames that layer
 * specifically instead of guessing from the enable order.
 */
export function noteRestoring(layer: string | null): void {
	if (layer === restoringLayer) return
	restoringLayer = layer
	patchSession({ restoring: layer ?? undefined })
}

/**
 * Heavy layers a recovery removed from the persisted state and still owes
 * the user. Persisted outside redux so a crash before they come back does
 * not lose them.
 */
export function setPendingRestore(layers: string[]): void {
	const record = readRecord()
	record.pending = [...layers]
	writeRecord(record)
}

/**
 * The session has proven stable (CrashRecovery: restore complete and the
 * stability window elapsed in the foreground). Forgets the crash loop so
 * the next launch starts at level 0 - no multi-day penalty for a device
 * that is working.
 */
export function markStable(): void {
	const record = readRecord()
	if (record.crashes.length === 0 && record.pending.length === 0) return
	record.crashes = []
	record.pending = []
	writeRecord(record)
	verdict = { ...verdict, history: [] }
}

/**
 * Install the sentinel. Call once, early in boot, before anything reads
 * crashVerdict().
 */
export function initCrashSentinel(): void {
	if (initialized || typeof window === "undefined") return
	initialized = true

	const now = Date.now()
	const record = readRecord()

	let previousTabSession: string | null = null
	try {
		previousTabSession = sessionStorage.getItem(cTabSessionKey)
	} catch { }

	// Scan previous sessions. This tab's own previous entry decides by
	// state alone (the browser reloaded us; "alive" means it never shut
	// down cleanly). Other ids: fresh entries are other live tabs and are
	// kept; stale "alive" entries are dead sessions; stale "clean" entries
	// are normal exits (including background jettisons, routine on mobile
	// and not a crash). document.wasDiscarded is deliberately not
	// consulted: Chromium sets it for routine background tab discards,
	// which are exactly the exits that must not count.
	let crashed: SessionEntry | null = null
	let sameTab = false
	const crashedIds = new Set<string>()
	for (const [id, entry] of Object.entries(record.sessions)) {
		const own = id === previousTabSession
		if (!own && now - entry.ts < cStaleMs) continue
		if (entry.state === "alive") {
			crashedIds.add(id)
			// Several dead sessions count once; the most recent one's layers tell the story
			if (!crashed || entry.ts > crashed.ts) crashed = entry
			if (own) sameTab = true
		}
		delete record.sessions[id]
	}

	// Loop detection: crashes inside the window accumulate, older ones drop
	record.crashes = record.crashes.filter(t => now - t < cLoopWindowMs)
	if (crashed) {
		record.crashes.push(now)
		if (record.crashes.length > cMaxCrashLevel) record.crashes.splice(0, record.crashes.length - cMaxCrashLevel)
	}

	if (crashed) {
		const heavy = Array.isArray(crashed.heavy) ? crashed.heavy : []
		const layers = [...heavy]
		for (const layer of record.pending) {
			if (!layers.includes(layer)) layers.push(layer)
		}
		const suspect = crashed.restoring ?? (heavy.length > 0 ? heavy[heavy.length - 1] : null)
		verdict = {
			level: Math.min(record.crashes.length, cMaxCrashLevel),
			heavyLayers: layers,
			suspect,
			sameTab,
			reason: crashed.reason ?? null,
			history: record.crashes.map(t => now - t),
		}
		console.warn(`[CrashSentinel] Previous session ended uncleanly (level ${verdict.level}${sameTab ? ", same tab" : ""}${verdict.reason ? `, ${verdict.reason}` : ""}); heavy layers: ${layers.join(", ") || "none"}; suspect: ${suspect ?? "none"}`)
	} else {
		// A clean start owes nothing: whatever a previous recovery had not
		// restored, the user has since exited normally without
		verdict = { level: 0, heavyLayers: [], suspect: null, sameTab: false, reason: null, history: record.crashes.map(t => now - t) }
		record.pending = []
	}

	// Register this session
	sessionId = crypto.randomUUID()
	bootTimeMs = performance.now()
	record.sessions[sessionId] = { ts: now, state: "alive", heavy: heavyLayersOn }
	writeRecord(record)
	try {
		sessionStorage.setItem(cTabSessionKey, sessionId)
	} catch { }

	// Recover breadcrumb trails left by dead sessions: a crashed session's
	// trail identifies the last instrumented checkpoint before the kill.
	// Trails from clean exits (and dead sessions generally) are deleted;
	// trails belonging to other live tabs are left alone.
	try {
		const crumbKeys: string[] = []
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i)
			if (key?.startsWith(cCrumbKeyPrefix)) crumbKeys.push(key)
		}
		for (const key of crumbKeys) {
			const id = key.slice(cCrumbKeyPrefix.length)
			if (record.sessions[id]) continue // another live tab's trail
			if (crashedIds.has(id)) {
				try {
					recoveredTrail = JSON.parse(localStorage.getItem(key) ?? "[]") as string[]
				} catch { }
			}
			localStorage.removeItem(key)
		}
	} catch { }

	// Clean-exit signals: pagehide covers navigation/close, visibilitychange
	// covers backgrounding (where a later OS kill is routine, not a crash).
	// On a same-tab reload the tab never goes hidden and Chromium fires a
	// trailing visibilitychange ("visible") AFTER pagehide - without the
	// unloading guard it would clobber the clean mark back to "alive" and
	// every reload would later count as a crash.
	let unloading = false
	window.addEventListener("pagehide", () => {
		unloading = true
		markSession("clean")
	})
	document.addEventListener("visibilitychange", () => {
		if (unloading) return
		markSession(document.visibilityState === "hidden" ? "clean" : "alive")
	})

	// Heartbeat keeps the alive timestamp fresh so other tabs (and the next
	// launch) can tell a live foreground tab from a dead one
	setInterval(() => {
		if (document.visibilityState === "visible") markSession("alive")
	}, cHeartbeatMs)
}

/**
 * Record a checkpoint in the crash breadcrumb trail. Called at every
 * allocation-heavy step of startup (grid fetches, worker compute phases,
 * chunked scene-object builds, heartbeats). Each crumb carries elapsed time
 * plus GPU texture MB (and JS heap MB where the browser exposes it), and the
 * trail is written synchronously to localStorage so it survives a tab kill.
 * No-op before initCrashSentinel() runs (e.g. the embed build).
 */
export function recordCrumb(tag: string): void {
	if (!sessionId) return

	const elapsed = ((performance.now() - bootTimeMs) / 1000).toFixed(1)
	let mem = ""
	const gpuMb = Math.round(textureMemoryStats().bytes / 1048576)
	if (gpuMb > 0) mem += ` gpu=${gpuMb}MB`
	const heap = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory
	if (heap) mem += ` heap=${Math.round(heap.usedJSHeapSize / 1048576)}MB`

	crumbs.push(`+${elapsed}s ${tag}${mem}`)
	if (crumbs.length > cMaxCrumbs) crumbs.splice(0, crumbs.length - cMaxCrumbs)
	try {
		localStorage.setItem(cCrumbKeyPrefix + sessionId, JSON.stringify(crumbs))
	} catch {
		// localStorage full or unavailable: the trail degrades gracefully
	}
}

/** Breadcrumb trail recovered from the session that crashed; null when the
 *  previous session exited cleanly or left no trail */
export function crashTrail(): string[] | null {
	return recoveredTrail
}

/** True when the previous session died without a clean shutdown */
export function lastSessionCrashed(): boolean {
	return verdict.level > 0
}

/** The verdict on the previous session, fixed at init */
export function crashVerdict(): CrashVerdict {
	return verdict
}

/** Diagnostic snapshot for window.__ebDebug */
export function crashSentinelSnapshot(): Record<string, unknown> {
	return {
		sessionId,
		verdict,
		heavyLayersOn,
		restoringLayer,
		crashReason,
		record: readRecord(),
		crumbs: [...crumbs],
		recoveredTrail,
	}
}

/**
 * Debug: plant a dead "alive" session so the next reload detects a crash
 * (exposed as window.__ebDebug.simulateCrash). Each reload that finds one
 * raises the level by one inside the loop window, so planting before three
 * successive reloads walks recovery through levels 1, 2 and 3. `restoring`
 * names the layer whose staggered restore was supposedly in flight;
 * `sameTab` plants it under this tab's id so it is found by the
 * same-tab rule rather than by staleness.
 */
export function plantSimulatedCrash(layers: string[] = ["cloud"], options: { restoring?: string, sameTab?: boolean } = {}): void {
	const record = readRecord()
	const id = `debug-${Date.now()}`
	record.sessions[id] = {
		ts: Date.now() - (options.sameTab ? 0 : cStaleMs * 2),
		state: "alive",
		heavy: layers,
		restoring: options.restoring,
	}
	writeRecord(record)
	if (options.sameTab) {
		try {
			sessionStorage.setItem(cTabSessionKey, id)
		} catch { }
	}
}
